import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSampleRequest } from "../context/SampleRequestContext";
import InlinePhoneCapture from "./InlinePhoneCapture";

const HERO_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/29860ed3-cbd1-403b-9bf5-d0da09992f9e/images/fda9d72b36f5f9aceee084bf95e0aad4738763c61ea27f8d82e603782c5a9e60.png";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1694378060976-66ee61c4f427?auto=format&fit=crop&w=2400&q=85";

export const Hero = () => {
  const navigate = useNavigate();
  const { openModal } = useSampleRequest();
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  const [imgSrc, setImgSrc] = useState(HERO_IMAGE);

  // Gentle parallax - translate image upward as user scrolls past hero
  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      // Only update while hero in or near viewport
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        const y = Math.max(-window.scrollY * 0.18, -120);
        setOffset(y);
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      aria-label="Aurevia Stones - Hero"
      className="relative w-full min-h-[100svh] overflow-hidden"
    >
      {/* Background image with parallax + slow pan */}
      <div
        className="absolute inset-0 will-change-transform animate-slow-pan"
        style={{
          transform: `translate3d(0, ${offset}px, 0)`,
        }}
      >
        <img
          src={imgSrc}
          onError={() => setImgSrc(HERO_FALLBACK)}
          alt="Premium beige marble slab with elegant gold veining"
          loading="eager"
          decoding="async"
          className="h-[115%] w-full object-cover object-center"
        />
      </div>

      {/* Gradient overlays - readability */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/90 via-brand-charcoal/55 to-brand-charcoal/20"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-brand-charcoal/60 via-transparent to-brand-charcoal"
      />

      {/* Grain texture */}
      <div className="grain absolute inset-0 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 h-full min-h-[100svh] flex items-center">
        <div className="editorial-container w-full">
          <div className="max-w-2xl md:max-w-3xl text-left md:py-24 py-20">
            {/* Headline */}
            <h1
              data-testid="hero-headline"
              className="font-serif font-light text-balance text-brand-cream leading-[0.95] tracking-[-0.015em]"
              style={{
                fontSize: "clamp(2.75rem, 7.2vw, 6.5rem)",
              }}
            >
              <span
                className="block animate-fade-up"
                style={{ animationDelay: "0.25s" }}
              >
                The quarry,
              </span>
              <span
                className="block italic text-brand-gold/95 mt-1 md:mt-2 animate-fade-up"
                style={{ animationDelay: "0.45s" }}
              >
                on your terms.
              </span>
            </h1>

            {/* Sub-headline */}
            <p
              data-testid="hero-subheadline"
              className="mt-8 md:mt-10 max-w-xl font-sans text-base md:text-lg leading-relaxed text-brand-cream/75 animate-fade-up"
              style={{ animationDelay: "0.7s" }}
            >
              We&apos;ve spent 30 years in India&apos;s marble heartland. Now
              we bring that access to you - shop from home, or visit
              Kishangarh with us.
            </p>

            {/* CTAs */}
            <div
              className="mt-10 md:mt-14 flex flex-col sm:flex-row gap-4 animate-fade-up"
              style={{ animationDelay: "0.9s" }}
            >
              <button
                type="button"
                onClick={() => navigate("/collection")}
                data-testid="hero-cta-browse-collection"
                className="group inline-flex items-center justify-center gap-3 bg-brand-gold px-8 py-4 text-brand-charcoal font-sans text-xs font-medium uppercase tracking-[0.24em] transition-all duration-300 hover:bg-brand-cream"
              >
                Browse Collection
                <ArrowRight
                  className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                  strokeWidth={2}
                />
              </button>

              <button
                type="button"
                onClick={() => openModal([], "hero_modal")}
                data-testid="hero-cta-request-samples"
                className="group inline-flex items-center justify-center gap-3 border border-brand-cream/30 px-8 py-4 text-brand-cream font-sans text-xs font-medium uppercase tracking-[0.24em] transition-all duration-300 hover:border-brand-gold hover:text-brand-gold hover:bg-white/[0.02]"
              >
                Request Free Samples
              </button>
            </div>

            {/* Visual separator */}
            <div
              className="mt-12 md:mt-16 mb-8 flex items-center justify-center gap-4 animate-fade-in"
              style={{ animationDelay: "1.1s" }}
            >
              <span className="h-px flex-1 max-w-[80px] bg-brand-cream/10" />
              <span className="h-px flex-1 max-w-[80px] bg-brand-cream/10" />
            </div>

            {/* Inline Phone Capture */}
            <div
              className="animate-fade-in"
              style={{ animationDelay: "1.2s" }}
            >
              <InlinePhoneCapture />
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
